import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, X, Check, Loader2, Copy, Zap,
  Database, ShoppingBag, BarChart2, CheckCircle2, Link2,
} from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";

const STEPS = [
  { id: "welcome",  label: "مرحباً",        icon: Zap },
  { id: "qoyod",    label: "قيود",           icon: Database },
  { id: "salla",    label: "سلة",            icon: ShoppingBag },
  { id: "done",     label: "اكتمل",          icon: CheckCircle2 },
];

/* Inline logo — mirrors Sidebar PlatformLogo */
function AppLogo({ size = 22 }) {
  const { logo_base64 } = usePlatform();
  if (logo_base64) return <img src={logo_base64} alt="logo" style={{ width: size, height: size }} className="rounded object-contain" />;
  return <Zap style={{ width: size, height: size }} className="text-white" />;
}

export default function Onboarding({ onDismiss }) {
  const { name } = usePlatform();
  const [step, setStep] = useState(0);
  const [qoyodKey, setQoyodKey] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [testing, setTesting] = useState(false);
  const [qoyodOk, setQoyodOk] = useState(false);
  const [sallaOk, setSallaOk] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/salla/status");
        setWebhookUrl(data?.webhook_url || "");
        if (data?.connected) setSallaOk(true);
      } catch {}
      try {
        const { data } = await api.get("/settings");
        if (data?.qoyod_api_key) { setQoyodKey(data.qoyod_api_key); setQoyodOk(true); }
      } catch {}
    })();
  }, []);

  const testQoyod = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/qoyod/test", { api_key: qoyodKey });
      setQoyodOk(true);
      toast.success(`تم الاتصال — ${data.branches_count} فرع`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };

  const [connecting, setConnecting] = useState(false);

  const connectSalla = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("auth_token") || "";
    if (!token) { toast.error("يجب تسجيل الدخول أولاً"); return; }

    const url = `${API_URL}/api/salla/connect?token=${encodeURIComponent(token)}`;
    const popup = window.open(url, "salla_connect", "width=520,height=660,scrollbars=yes");
    if (!popup) { window.location.href = url; return; }

    setConnecting(true);
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get("/salla/status");
        if (data?.connected) {
          clearInterval(poll);
          setConnecting(false);
          setSallaOk(true);
          popup.close();
          toast.success("تم ربط سلة بنجاح!");
        }
      } catch {}
      if (popup.closed) { clearInterval(poll); setConnecting(false); }
    }, 2000);
    setTimeout(() => { clearInterval(poll); setConnecting(false); }, 180000);
  };

  const copyWebhook = () => {
    if (!webhookUrl) return;
    navigator.clipboard.writeText(webhookUrl);
    toast.success("تم نسخ رابط Webhook");
  };

  const runImport = async () => {
    setImporting(true);
    try {
      await api.post("/qoyod/import", { api_key: qoyodKey });
      toast.success("بدأ استيراد بيانات قيود");
      setTimeout(() => { setImportDone(true); setImporting(false); }, 2000);
    } catch (e) {
      toast.error(formatApiError(e));
      setImporting(false);
    }
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden fade-up">

        {/* ── Header ── */}
        <div className="bg-[#0C0C0F] px-7 py-5 relative">
          <button
            onClick={onDismiss}
            className="absolute top-4 left-4 p-1.5 text-white/30 hover:text-white/80 transition-colors rounded-lg hover:bg-[var(--bg-card-hover)]/10"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-[#004EEB] rounded-xl grid place-items-center shadow-[0_0_18px_rgba(0,78,235,0.5)]">
              <AppLogo size={20} />
            </div>
            <div>
              <span className="text-white font-bold text-base">{name}</span>
              <span className="text-white/40 text-sm font-normal"> — إعداد أولي</span>
            </div>
          </div>
          {/* Step indicators */}
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2">
                <div
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                    i === step
                      ? "bg-[#004EEB] text-white"
                      : i < step
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-white/10 text-white/30"
                  }`}
                >
                  {i < step ? <Check className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
                  {s.label}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-px w-4 transition-colors ${i < step ? "bg-emerald-500/40" : "bg-white/10"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-7 py-6 min-h-[320px] flex flex-col">

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="flex-1 space-y-5 fade-up">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#004EEB] rounded-xl grid place-items-center shadow-[0_0_16px_rgba(0,78,235,0.3)]">
                    <AppLogo size={22} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">مرحباً في {name}</h2>
                </div>
                <p className="text-slate-500 text-sm leading-relaxed">
                  سنساعدك على ربط نظام قيود المحاسبي بمتجرك في سلة خلال دقيقتين فقط.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Database,  title: "قيود",    desc: "مصدر المخزون",       color: "bg-blue-50 text-[#004EEB]"   },
                  { icon: ShoppingBag, title: "سلة",  desc: "وجهة المزامنة",      color: "bg-indigo-50 text-indigo-600" },
                  { icon: BarChart2, title: "تقارير", desc: "تحليلات لحظية",      color: "bg-emerald-50 text-emerald-600" },
                  { icon: Zap,       title: "تلقائي", desc: "تزامن كل 15 دقيقة",  color: "bg-amber-50 text-amber-600"   },
                ].map(({ icon: Icon, title, desc, color }) => (
                  <div key={title} className="bg-slate-50 border border-slate-100 rounded-xl p-4 hover:border-slate-200 transition-colors">
                    <div className={`w-8 h-8 rounded-lg grid place-items-center ${color} mb-2`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Qoyod */}
          {step === 1 && (
            <div className="flex-1 space-y-4 fade-up">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 bg-blue-50 rounded-lg grid place-items-center">
                    <Database className="w-3.5 h-3.5 text-[#004EEB]" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">ربط قيود</h2>
                  {qoyodOk && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                </div>
                <p className="text-slate-500 text-sm">أدخل مفتاح API من قيود (الإعدادات العامة → API).</p>
              </div>
              <div className="space-y-2">
                <label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-slate-500">Qoyod API Key</label>
                <div className="flex gap-2">
                  <input
                    data-testid="onboarding-qoyod-key-input"
                    type="text"
                    value={qoyodKey}
                    onChange={(e) => setQoyodKey(e.target.value)}
                    placeholder="c4151..."
                    className="flex-1 border border-slate-200 rounded-xl px-4 py-3 text-sm mono focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10"
                  />
                  <button
                    onClick={testQoyod}
                    disabled={testing || !qoyodKey}
                    className="px-4 py-3 border border-slate-200 rounded-xl text-sm font-medium hover:border-slate-400 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                  >
                    {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : "اختبار"}
                  </button>
                </div>
              </div>
              {qoyodOk && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  تم الاتصال بقيود بنجاح
                </div>
              )}
            </div>
          )}

          {/* Step 2: Salla — simplified (platform credentials stored in env) */}
          {step === 2 && (
            <div className="flex-1 space-y-4 fade-up">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 bg-indigo-50 rounded-lg grid place-items-center">
                    <ShoppingBag className="w-3.5 h-3.5 text-indigo-600" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">ربط سلة</h2>
                  {sallaOk && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                </div>
                <p className="text-slate-500 text-sm">اضغط الزر لتوصيل متجرك في سلة مباشرة.</p>
              </div>

              {sallaOk ? (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  تم الربط بسلة بنجاح
                </div>
              ) : (
                <button
                  onClick={connectSalla}
                  disabled={connecting}
                  data-testid="onboarding-connect-salla-btn"
                  className="w-full flex items-center justify-center gap-2.5 py-4 bg-[#004EEB] hover:bg-[#0040C1] disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-all shadow-lg shadow-[#004EEB]/25"
                >
                  {connecting
                    ? <><Loader2 className="w-4 h-4 animate-spin" />جارٍ الانتظار...</>
                    : <><ShoppingBag className="w-4 h-4" />ربط متجر سلة</>}
                </button>
              )}

              {/* Webhook URL */}
              {webhookUrl && (
                <div className="border border-dashed border-slate-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    <Link2 className="w-3 h-3" />
                    رابط Webhook (للتنبيهات)
                  </div>
                  <div className="flex gap-2">
                    <input readOnly value={webhookUrl}
                      className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-xs mono bg-slate-50 focus:outline-none" />
                    <button onClick={copyWebhook}
                      className="px-3 py-2 border border-slate-200 rounded-xl text-slate-500 hover:border-slate-400 transition-colors">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div className="flex-1 flex flex-col items-center justify-center text-center space-y-5 fade-up">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl grid place-items-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">الإعداد مكتمل!</h2>
                <p className="text-slate-500 mt-2 text-sm">المنصة جاهزة. يمكنك الآن استيراد بيانات قيود لتفعيل المزامنة.</p>
              </div>
              <button
                onClick={runImport}
                disabled={importing || importDone || !qoyodOk}
                className="flex items-center gap-2 px-5 py-3 bg-[#004EEB] hover:bg-[#0040C1] text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-50 shadow-lg shadow-[#004EEB]/20"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : importDone ? <Check className="w-4 h-4" /> : <Database className="w-4 h-4" />}
                {importDone ? "تم الاستيراد" : importing ? "جارٍ الاستيراد…" : "استيراد بيانات قيود"}
              </button>
              {!qoyodOk && <p className="text-xs text-amber-600">يلزم ربط قيود أولاً في الخطوة الثانية</p>}
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="border-t border-slate-100 px-7 py-4 flex items-center justify-between">
          <div>
            {step > 0 && step < STEPS.length - 1 && (
              <button
                onClick={back}
                className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:border-slate-400 transition-colors"
              >
                <ArrowRight className="w-3.5 h-3.5" />
                السابق
              </button>
            )}
          </div>
          <div>
            {step < STEPS.length - 1 ? (
              <button
                onClick={next}
                className="flex items-center gap-1.5 px-5 py-2 bg-[#004EEB] hover:bg-[#0040C1] text-white rounded-xl text-sm font-semibold transition-all shadow-sm shadow-[#004EEB]/20"
              >
                {step === 0 ? "لنبدأ" : "التالي"}
                <ArrowLeft className="w-3.5 h-3.5 rtl-flip" />
              </button>
            ) : (
              <button
                onClick={onDismiss}
                className="flex items-center gap-1.5 px-5 py-2 bg-[#0C0C0F] hover:bg-slate-800 text-white rounded-xl text-sm font-semibold transition-all"
              >
                <Check className="w-3.5 h-3.5" />
                الذهاب للوحة التحكم
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
